// Projection utilities for the editor canvas.
//
// Design:
// - Upstream (Rosreestr) gives polygons in WGS84 [lon, lat].
// - Editor works entirely in LOCAL METRES (x east, y south) so that object
//   positions, shadow math, and SNiP distances all use the same unit.
// - Conversion from geographic → local metres uses a local tangent plane
//   around the polygon centroid (equirectangular at small scales is accurate
//   to <1 mm for plot-sized areas and has no Mercator latitude distortion).
// - Conversion from metres → pixels is a plain fit-to-stage with margin.

const EARTH_RADIUS_M = 6_378_137
const MARGIN_PX = 32

export function centroidLngLat(polygonLngLat) {
  if (!polygonLngLat || polygonLngLat.length === 0) return null
  const lng = polygonLngLat.reduce((s, p) => s + p[0], 0) / polygonLngLat.length
  const lat = polygonLngLat.reduce((s, p) => s + p[1], 0) / polygonLngLat.length
  return [lng, lat]
}

export function projectPolygonToLocalMetres(polygonLngLat, origin = null) {
  if (!polygonLngLat || polygonLngLat.length === 0) return { polygonM: [], originLngLat: null }

  const [lngOrigin, latOrigin] = origin ?? centroidLngLat(polygonLngLat)
  const cosLat = Math.cos((latOrigin * Math.PI) / 180)
  const degToRad = Math.PI / 180

  const polygonM = polygonLngLat.map(([lng, lat]) => [
    (lng - lngOrigin) * degToRad * EARTH_RADIUS_M * cosLat,
    // Screen y grows downward; latitude grows upward — flip sign.
    -((lat - latOrigin) * degToRad * EARTH_RADIUS_M),
  ])
  return { polygonM, originLngLat: [lngOrigin, latOrigin] }
}

// Inverse of projectPolygonToLocalMetres. Needed when showing a stored plot
// back on a map (Stage 2b).
export function unprojectPolygonFromLocalMetres(polygonM, originLngLat) {
  if (!polygonM || polygonM.length === 0 || !originLngLat) return []
  const [lngOrigin, latOrigin] = originLngLat
  const cosLat = Math.cos((latOrigin * Math.PI) / 180)
  const radToDeg = 180 / Math.PI

  return polygonM.map(([xM, yM]) => [
    lngOrigin + (xM / (EARTH_RADIUS_M * cosLat)) * radToDeg,
    latOrigin - (yM / EARTH_RADIUS_M) * radToDeg,
  ])
}

export function polygonBoundingBox(polygonM) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const [x, y] of polygonM) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY, widthM: maxX - minX, heightM: maxY - minY }
}

// Fits a polygon (or arbitrary width/height in metres) inside a stage with margin.
// Returns scale + offset such that metresToPx puts the polygon centred.
export function computeScale(stageWidthPx, stageHeightPx, plotWidthM, plotHeightM, plotOriginM = { x: 0, y: 0 }) {
  const available = {
    w: Math.max(stageWidthPx - MARGIN_PX * 2, 1),
    h: Math.max(stageHeightPx - MARGIN_PX * 2, 1),
  }
  const pxPerMetre = Math.min(available.w / plotWidthM, available.h / plotHeightM)
  const plotPx = { w: plotWidthM * pxPerMetre, h: plotHeightM * pxPerMetre }
  const offsetPx = {
    x: (stageWidthPx - plotPx.w) / 2 - plotOriginM.x * pxPerMetre,
    y: (stageHeightPx - plotPx.h) / 2 - plotOriginM.y * pxPerMetre,
  }
  return { pxPerMetre, offsetPx }
}

export function metresToPx({ xM, yM }, { pxPerMetre, offsetPx }) {
  return {
    x: offsetPx.x + xM * pxPerMetre,
    y: offsetPx.y + yM * pxPerMetre,
  }
}

export function pxToMetres({ x, y }, { pxPerMetre, offsetPx }) {
  return {
    xM: (x - offsetPx.x) / pxPerMetre,
    yM: (y - offsetPx.y) / pxPerMetre,
  }
}
